// src/pages/CreatorDashboard.js
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import AnalyticsService from '../services/AnalyticsService';

/* ── Design tokens ─────────────────────────────────────────────────────────── */
const C = {
  coral:    'var(--primary-color)',
  coralDim: 'var(--primary-dim)',
  bg:       'var(--bg-dark)',
  surface1: 'var(--bg-card)',
  surface2: 'var(--bg-elevated)',
  border:   'var(--border-color)',
  text:     'var(--text-primary)',
  muted:    'var(--text-secondary)',
  faint:    'var(--text-mute)',
};

function getInitials(username, email) {
  const name = username ?? email ?? '?';
  const parts = name.split(/[\s@._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function Avatar({ initials, size = 56 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 2,
      background: 'linear-gradient(135deg, #FF6B35 0%, #1a1a2e 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <span style={{ fontSize: size * 0.35, fontWeight: 800, color: '#fff', fontFamily: "'Manrope', sans-serif" }}>
        {initials}
      </span>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: C.muted,
      marginBottom: 10, marginTop: 4,
    }}>{children}</div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{
      background: C.surface1, borderRadius: 16,
      border: `1px solid ${C.border}`, overflow: 'hidden',
      marginBottom: 20, ...style,
    }}>{children}</div>
  );
}

function StatCard({ label, value, sub, coral }) {
  return (
    <div style={{
      background: C.surface1, border: `1px solid ${C.border}`,
      borderRadius: 16, padding: '16px 20px', textAlign: 'center',
    }}>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace", fontSize: 26, fontWeight: 800,
        color: coral ? C.coral : C.text, letterSpacing: -1, marginBottom: 4,
      }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.muted }}>
        {label}
      </div>
      {sub && <div style={{ fontSize: 11, color: C.faint, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

/* ── Mini bar chart ─────────────────────────────────────────────────────────── */
function EarningsBar({ label, value, max, dkk }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 13 }}>
        <span style={{ color: C.text, fontWeight: 600 }}>{label}</span>
        <span style={{ color: C.coral, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
          {dkk.toFixed(2)} kr
        </span>
      </div>
      <div style={{ height: 6, background: C.surface2, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: C.coral, borderRadius: 3, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function CreatorDashboard() {
  const [activeTab, setActiveTab]         = useState('overblik');
  const [podcasts, setPodcasts]           = useState([]);
  const [analytics, setAnalytics]         = useState(null);
  const [loading, setLoading]             = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [error, setError]                 = useState('');

  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'podcasts'), where('creatorId', '==', currentUser.uid))
        );
        setPodcasts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        setError('Kunne ikke hente podcasts: ' + err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      try {
        const data = await AnalyticsService.getCreatorAnalytics(currentUser.uid);
        setAnalytics(data);
      } catch (err) {
        console.error('Analytics error:', err);
      } finally {
        setAnalyticsLoading(false);
      }
    })();
  }, [currentUser]);

  function episodeCount(podcast) {
    if (!podcast.episodes) return 0;
    return Array.isArray(podcast.episodes)
      ? podcast.episodes.length
      : Object.keys(podcast.episodes).length;
  }

  const initials  = getInitials(userProfile?.username, currentUser?.email);
  const totalEps  = podcasts.reduce((t, p) => t + episodeCount(p), 0);
  const monthLabel = new Date().toLocaleDateString('da-DK', { month: 'long', year: 'numeric' });

  const TABS = [
    { key: 'overblik',  label: 'Overblik' },
    { key: 'analyse',   label: 'Analyse' },
    { key: 'indtjening',label: 'Indtjening' },
  ];

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '24px 20px 80px', maxWidth: 800, margin: '0 auto' }}>

      {/* ── Header ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <Avatar initials={initials} size={56} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 18, color: C.text, marginBottom: 2 }}>
            {userProfile?.username ?? currentUser?.email?.split('@')[0] ?? '—'}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={{
              padding: '3px 10px', borderRadius: 9999,
              background: C.coralDim, border: '1px solid rgba(255,107,53,0.4)',
              fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: C.coral,
            }}>🎙️ Skaber</span>
            {userProfile?.isAdmin && (
              <span style={{
                padding: '3px 10px', borderRadius: 9999,
                background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.4)',
                fontSize: 11, fontWeight: 700, color: '#a78bfa',
              }}>🛡️ Admin</span>
            )}
          </div>
        </div>
        <Link to="/podcast/import" className="fc-cta-primary" style={{ fontSize: 13, padding: '10px 16px', flexShrink: 0 }}>
          + Importer podcast
        </Link>
      </div>

      {/* ── Tab toggle ──────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 4,
        background: C.surface1, border: `1px solid ${C.border}`,
        borderRadius: 12, padding: 4, marginBottom: 24,
      }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            flex: 1, padding: '8px 0', borderRadius: 9,
            border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 700,
            transition: 'all 0.15s',
            background: activeTab === tab.key ? C.coral : 'transparent',
            color: activeTab === tab.key ? '#fff' : C.muted,
          }}>{tab.label}</button>
        ))}
        <button onClick={() => navigate('/podcast-management')} style={{
          flex: 1, padding: '8px 0', borderRadius: 9,
          border: `1px solid ${C.border}`, cursor: 'pointer',
          fontSize: 13, fontWeight: 700,
          background: 'transparent', color: C.muted,
          transition: 'all 0.15s',
        }}>Mine podcasts →</button>
      </div>

      {error && (
        <div style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 10, padding: '10px 14px', fontSize: 14, color: '#f87171', marginBottom: 20 }}>
          {error}
        </div>
      )}

      {/* ══════════════════════ OVERBLIK ══════════════════════════ */}
      {activeTab === 'overblik' && (
        <>
          <SectionHeading>{monthLabel}</SectionHeading>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
            <StatCard label="Podcasts"  value={loading ? '—' : podcasts.length} sub="aktive shows" />
            <StatCard label="Episoder"  value={loading ? '—' : totalEps} sub="publicerede" />
            <StatCard label="Månedlig indtjening" value={analyticsLoading ? '—' : `${analytics?.monthlyEarnings ?? 0}`} sub="DKK denne måned" coral />
            <StatCard label="Lyttere"  value={analyticsLoading ? '—' : (analytics?.totalListeners ?? 0)} sub="unikke lyttere" />
          </div>

          {/* Podcast list */}
          <SectionHeading>Dine podcasts</SectionHeading>
          {loading ? (
            <Card><div style={{ padding: 24, textAlign: 'center', color: C.faint }}>Indlæser…</div></Card>
          ) : podcasts.length > 0 ? (
            <Card>
              {podcasts.map((p, i) => (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 16px',
                  borderBottom: i < podcasts.length - 1 ? `1px solid ${C.border}` : 'none',
                }}>
                  {p.imageURL ? (
                    <img src={p.imageURL} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{
                      width: 48, height: 48, borderRadius: 10,
                      background: C.surface2, border: `1px solid ${C.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, flexShrink: 0,
                    }}>🎙️</div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: C.text, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.title}
                    </div>
                    <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>
                      {episodeCount(p)} episode{episodeCount(p) !== 1 ? 'r' : ''}
                      {p.category ? ` · ${p.category}` : ''}
                    </div>
                  </div>
                  <Link
                    to={`/podcast/${p.id}/manage`}
                    style={{
                      fontSize: 12, fontWeight: 700, color: C.coral,
                      textDecoration: 'none', flexShrink: 0, padding: '6px 12px',
                      border: `1px solid rgba(255,107,53,0.3)`, borderRadius: 8,
                    }}
                  >
                    Administrer
                  </Link>
                </div>
              ))}
            </Card>
          ) : (
            <Card>
              <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🎙️</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.muted, marginBottom: 6 }}>Ingen podcasts endnu</div>
                <div style={{ fontSize: 13, color: C.faint, marginBottom: 20 }}>
                  Importer dit første podcast og begynd at optjene 80% af din lyttertid.
                </div>
                <Link to="/podcast/import" className="fc-cta-primary" style={{ display: 'inline-flex', fontSize: 13 }}>
                  Importer podcast
                </Link>
              </div>
            </Card>
          )}

          {/* Revenue model reminder */}
          <Card style={{ padding: 20 }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{
                width: 44, height: 44, borderRadius: 10,
                background: C.coralDim, border: '1px solid rgba(255,107,53,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, flexShrink: 0,
              }}>💡</div>
              <div>
                <div style={{ fontWeight: 700, color: C.text, marginBottom: 6 }}>Sådan beregnes din indtjening</div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.65 }}>
                  Lyttere betaler 30 kr/md. 80% — 24 kr — fordeles proportionalt efter lyttetid.
                  Bruger en lytter 50% af sin tid på dit podcast, får du 50% af hans 24 kr = 12 kr fra den lytter.
                </div>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* ══════════════════════ ANALYSE ════════════════════════════ */}
      {activeTab === 'analyse' && (
        <>
          <SectionHeading>Lytteperformance</SectionHeading>

          {analyticsLoading ? (
            <Card><div style={{ padding: 32, textAlign: 'center', color: C.faint }}>Indlæser analyse…</div></Card>
          ) : analytics && (analytics.totalListeners ?? 0) > 0 ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                <StatCard label="Lyttetimer i alt"     value={`${analytics.totalListeningHours ?? 0}t`} coral />
                <StatCard label="Denne måned"           value={`${analytics.monthlyListeningHours ?? 0}t`} />
                <StatCard label="Gns. pr. lytter"
                  value={`${analytics.totalListeners > 0 ? Math.round((analytics.totalListeningTime ?? 0) / analytics.totalListeners / 60) : 0} min`} />
              </div>

              <SectionHeading>Publikum</SectionHeading>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <StatCard label="Unikke lyttere"  value={analytics.totalListeners ?? 0} coral />
                <StatCard label="Dine podcasts"   value={podcasts.length} />
              </div>

              <SectionHeading>Senest opdateret</SectionHeading>
              <Card style={{ padding: '14px 18px' }}>
                <div style={{ fontSize: 13, color: C.muted }}>
                  {analytics.lastUpdated
                    ? new Date(analytics.lastUpdated).toLocaleString('da-DK')
                    : 'Ukendt'}
                </div>
              </Card>
            </>
          ) : (
            <Card>
              <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.muted, marginBottom: 6 }}>Ingen analysedata endnu</div>
                <div style={{ fontSize: 13, color: C.faint }}>
                  Din detaljerede analyse vises her når lyttere begynder at høre dine podcasts.
                  <br />Aktuelle podcasts: {podcasts.length}
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* ══════════════════════ INDTJENING ═════════════════════════ */}
      {activeTab === 'indtjening' && (
        <>
          <SectionHeading>Betalingsoversigt</SectionHeading>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
            <StatCard label="Denne måned"  value={analyticsLoading ? '—' : `${analytics?.monthlyEarnings ?? 0}`} sub="DKK" coral />
            <StatCard label="I alt"        value={analyticsLoading ? '—' : `${analytics?.totalEarnings ?? 0}`}   sub="DKK" />
            <StatCard label="Pr. lytter"
              value={analyticsLoading || !analytics?.totalListeners
                ? '—'
                : `${Math.round((analytics.totalEarnings / analytics.totalListeners) * 100) / 100}`}
              sub="DKK gns." />
          </div>

          {/* 80/20 breakdown */}
          <SectionHeading>Fordelingsnøgle</SectionHeading>
          <Card style={{ padding: 20 }}>
            {[
              { label: 'Til skaberne (80%)', value: 80, dkk: 24 },
              { label: 'Faircast drift (20%)', value: 20, dkk: 6 },
            ].map(row => (
              <EarningsBar key={row.label} label={row.label} value={row.value} max={100} dkk={row.dkk} />
            ))}
            <div style={{
              marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`,
              display: 'flex', justifyContent: 'space-between', fontSize: 13,
            }}>
              <span style={{ color: C.faint }}>Per abonnent pr. måned</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: C.text }}>30,00 kr</span>
            </div>
          </Card>

          {analytics && (analytics.totalEarnings ?? 0) > 0 && (
            <>
              <SectionHeading>Detaljer</SectionHeading>
              <Card>
                {[
                  { label: 'Samlet lyttetid', value: `${analytics.totalListeningHours ?? 0} timer` },
                  { label: 'Skaber-andel', value: '80%' },
                  { label: 'Abonnementspris', value: '30 kr/md.' },
                  { label: 'Gns. pr. lytter', value: `${analytics.totalListeners > 0 ? Math.round((analytics.totalEarnings / analytics.totalListeners) * 100) / 100 : 0} kr` },
                ].map((row, i, arr) => (
                  <div key={row.label} style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '12px 18px',
                    borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none',
                    fontSize: 14,
                  }}>
                    <span style={{ color: C.muted }}>{row.label}</span>
                    <span style={{ fontWeight: 700, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>{row.value}</span>
                  </div>
                ))}
              </Card>
            </>
          )}

          {!analyticsLoading && !(analytics?.totalEarnings > 0) && (
            <Card>
              <div style={{ padding: '32px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>💰</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.muted, marginBottom: 6 }}>Ingen indtjening endnu</div>
                <div style={{ fontSize: 13, color: C.faint }}>
                  Din indtjening vises her når lyttere abonnerer og hører dine podcasts.
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
